import type { ApiErrorBody, ErrorCode } from '@nivex/contracts'

export type ErrorKind =
  | 'network'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'validation'
  | 'conflict'
  | 'server'
  | 'unknown'

export interface AppErrorOptions {
  status?: number
  cause?: unknown
  code?: ErrorCode
  requestId?: string
  fieldErrors?: Record<string, string[]>
}

export class AppError extends Error {
  readonly kind: ErrorKind
  readonly status?: number
  readonly cause?: unknown
  /** Machine-readable code from the backend's error envelope — absent for client-side errors (network, timeout). */
  readonly code?: ErrorCode
  /** Correlates this failure to the backend's logs — surface it in any "something went wrong" UI. */
  readonly requestId?: string
  /** Dotted paths (`"holdings.0.units"`) — pass straight to React Hook Form's `setError`. */
  readonly fieldErrors?: Record<string, string[]>

  constructor(kind: ErrorKind, message: string, options?: AppErrorOptions) {
    super(message)
    this.name = 'AppError'
    this.kind = kind
    this.status = options?.status
    this.cause = options?.cause
    this.code = options?.code
    this.requestId = options?.requestId
    this.fieldErrors = options?.fieldErrors
  }

  static fromUnknown(error: unknown): AppError {
    if (error instanceof AppError) return error

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return new AppError('timeout', 'The request took too long.', { cause: error })
      }
      if (error instanceof TypeError) {
        // A bare TypeError ("Failed to fetch" / "Load failed") is what `fetch` throws for DNS
        // failures, offline connections, and CORS rejections — there is no HTTP response at all.
        return new AppError('network', "We couldn't reach the server. Check your connection.", {
          cause: error,
        })
      }
      return new AppError('unknown', error.message, { cause: error })
    }

    return new AppError('unknown', 'Something unexpected happened.', { cause: error })
  }

  /** `body` is whatever the response's JSON parsed to — never assume it matches `ApiErrorBody`, a non-2xx response might not even be from this API. */
  static fromResponse(status: number, body?: Partial<ApiErrorBody> | null): AppError {
    const message = body?.message
    const options: AppErrorOptions = {
      status,
      code: body?.code,
      requestId: body?.requestId,
      fieldErrors: body?.fieldErrors,
    }

    if (status === 401) {
      return new AppError('unauthorized', message ?? 'Please sign in again.', options)
    }
    if (status === 403) {
      return new AppError('forbidden', message ?? "You don't have access to this.", options)
    }
    if (status === 404) {
      return new AppError('not-found', message ?? "We couldn't find that.", options)
    }
    if (status === 422 || status === 400) {
      return new AppError(
        'validation',
        message ?? 'Please check the information you entered.',
        options,
      )
    }
    if (status === 409) {
      return new AppError(
        'conflict',
        message ?? 'This already exists or was already done.',
        options,
      )
    }
    if (status >= 500) {
      return new AppError('server', message ?? 'Something went wrong on our end.', options)
    }
    return new AppError('unknown', message ?? 'Something unexpected happened.', options)
  }

  /** 4xx errors (other than 408/429) are not worth retrying automatically. */
  get isRetryable(): boolean {
    // Prisma P2034 (a transient serialization failure) — the backend documents this as safe to
    // retry regardless of the HTTP status it happens to be mapped to.
    if (this.code === 'TRANSIENT_CONFLICT') return true
    if (this.status == null) return true
    if (this.status === 408 || this.status === 429) return true
    return this.status < 400 || this.status >= 500
  }

  /** Errors severe enough that a rendered widget should escalate to a boundary. */
  get isFatal(): boolean {
    return this.kind === 'server' || this.kind === 'unknown'
  }
}
